import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './Navbar.css';
import AuthModal from './AuthModal';
import { useAuth } from '../components/AuthContext';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import useCartContext from '../hooks/useCartContext'; // Importar el hook del carrito

const Navbar = ({ cartItemCount }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  // Obtener datos del usuario desde AuthContext (ya los cargó)
  const { currentUser, logout, userData: authUserData } = useAuth();
  const { clearCart } = useCartContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });

  // Detectar el scroll para cambiar el estilo del navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Usar datos del usuario desde AuthContext
  useEffect(() => {
    if (authUserData) {
      const userName = `${authUserData.name} ${authUserData.apellido}`;
      setUserData({ ...authUserData, userName });
    } else {
      setUserData(null);
    }
  }, [authUserData]);

  // Mostrar el modal de autenticación
  const handleAuthClick = () => {
    setShowAuthModal(true);
  };

  // Cerrar el modal de autenticación
  const closeModal = () => {
    setShowAuthModal(false);
  };

  // Toggle para el dropdown del usuario
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Manejar el cierre de sesión
  const handleLogout = async () => {
    try {
      await logout();
      clearCart(); // Limpiar el carrito cuando el usuario cierra sesión
      setShowDropdown(false);
      navigate('/'); // Redirigir a la tienda después de cerrar sesión
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  // Navegar a la tienda
  const navigateToStore = () => {
    navigate('/'); // Navegar a la tienda
    setShowDropdown(false);
  };

  // Navegar al carrito
  const navigateToCart = () => {
    navigate('/cart'); // Navegar al carrito
  };

  // Manejar el cambio de estado para recibir notificaciones
  const toggleNotification = async () => {
    const updatedUserData = {
      ...userData,
      recibirNotificaciones: !userData?.recibirNotificaciones,
    };

    setUserData(updatedUserData);

    if (currentUser) {
      try {
        const db = getFirestore();
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { recibirNotificaciones: !userData?.recibirNotificaciones });
        showNotification(
          updatedUserData.recibirNotificaciones
            ? 'Has activado las notificaciones.'
            : 'Has desactivado las notificaciones.',
            updatedUserData.recibirNotificaciones ? 'success' : 'error' // Cambia el estilo según el estado
        );
      } catch (error) {
        console.error('Error al actualizar las preferencias del usuario:', error);
        showNotification('Error al cambiar las preferencias de notificaciones.', 'error');
      }
    }
  };

   // Mostrar una notificación
   const showNotification = (message, type) => {
    setNotification({ message, type, visible: true });

    // Ocultar la notificación automáticamente después de 3 segundos
    setTimeout(() => {
      setNotification((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  return (
    <>
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
        {/* Mostrar ícono de retroceso en ciertas rutas */}
        {location.pathname === '/edit-profile' || location.pathname === '/edit-product' || location.pathname === '/cart' ? (
          <i className="fas fa-arrow-left back-icon" onClick={navigateToStore}></i>
        ) : (
          <img src={`${process.env.PUBLIC_URL}/img/KIOSCO.png`} alt="Logo" className="logo" />
        )}

        <div className="title-container">
          <h1 className="title">Sol y Luna</h1>
          <span className="description">KIOSCO</span>
        </div>

        <div className="auth-cart-container">
          {/* Verificar si el usuario está autenticado */}
          {currentUser ? (
            <div className="user-menu-container">
              <span className="auth-link auth-link-text" onClick={toggleDropdown}>
                Bienvenido, {userData ? userData.userName : currentUser.email}{' '}
                <i className={`fas fa-caret-${showDropdown ? 'up' : 'down'}`}></i>
              </span>
              <span className="auth-link auth-link-icon" onClick={toggleDropdown}>
                <i className={`fas fa-user ${showDropdown ? 'fa-caret-up' : 'fa-caret-down'}`}></i>
              </span>
              {/* Dropdown para el usuario */}
              {showDropdown && (
                <div className="dropdown-menu">
                  {location.pathname === '/edit-profile' || location.pathname === '/cart' ? (
                    <span className="dropdown-item" onClick={navigateToStore}>
                      Ir a tienda
                    </span>
                  ) : (
                    <span className="dropdown-item" onClick={() => navigate('/edit-profile')}>
                      Editar perfil
                    </span>
                  )}
                  {/* Mostrar Panel de administración si el usuario es admin */}
                  {userData && (userData.role === 'admin' || userData.role === 'employee') && (
                    <span className="dropdown-item" onClick={() => navigate('/admin')}>
                      Panel de administración
                    </span>
                  )}
                  <span className="dropdown-item" onClick={() => navigate('/client-orders')}>
                    Mis pedidos
                  </span>
                  <span className="dropdown-item" onClick={handleLogout}>
                    Cerrar sesión
                  </span>
                </div>
              )}
            </div>
          ) : (
            <>
              <span onClick={handleAuthClick} className="auth-link auth-link-text">
                Ingresar o Registrarse
              </span>
              <span onClick={handleAuthClick} className="auth-link auth-link-icon">
                <i className="fas fa-user"></i>
              </span>
            </>
          )}
          {/* Mostrar ícono de campanita solo si el usuario ha iniciado sesión */}
          {currentUser && (
            <div
              className={`notification-icon-container ${
                userData?.recibirNotificaciones ? 'active' : 'inactive'
              }`}
              onClick={toggleNotification}
            >
              {userData?.recibirNotificaciones ? (
                <i className="fas fa-bell"></i>
              ) : (
                <i className="fas fa-bell-slash"></i>
              )}
            </div>
          )}

          {/* Mostrar ícono de carrito con el número de productos */}
          <div className="cart-icon-container" onClick={navigateToCart}>
            <i className="fas fa-shopping-cart"></i>
            {cartItemCount > 0 && <span className="cart-count">{cartItemCount}</span>}
          </div>
        </div>
      </nav>

      {/* Modal de autenticación */}
      {showAuthModal && <AuthModal onAuthSuccess={closeModal} onClose={closeModal} />}
      {notification.visible && (
        <div className={`notification ${notification.type}`}>{notification.message}</div>
      )}
    </>
  );
};

export default Navbar;





